import type { PresentationTarget, Rectangle, Insets } from './video-presentation.js';
import type { SocialVideoPresentation } from './social-presentation.js';

/** Frozen organic preview examples shared by composition framing and preview chrome. */
export type ChromeDensity = 'normal' | 'busy';
export interface ChromeRegion { name: string; kind: 'account' | 'caption' | 'actions' | 'playback'; bounds: Rectangle }

/** Wide overlays retain the typography scale of the frame's shorter edge. */
export function compositionOverlayUnit(width: number, height: number): number {
  return Math.min(width, height) / 1080;
}
export function socialChromeRegions(target: PresentationTarget, width: number, height: number, density: ChromeDensity): ChromeRegion[] {
  if (target === 'none') return [];
  const unit = width / 1080;
  const busy = density === 'busy';
  const story = target === 'instagram-stories';
  const shortFeed = target === 'instagram-feed' && width / height > .6;
  const regions: ChromeRegion[] = [{ name: 'Account and audio labels', kind: 'account',
    bounds: { x: 0, y: 0, width, height: (busy ? 310 : 182) * unit } }];
  if (story) return regions;
  const bottom = shortFeed ? 36 : 72;
  const captionHeight = shortFeed ? busy ? 230 : 100 : busy ? 480 : 200;
  regions.push({ name: 'Caption and audio labels', kind: 'caption', bounds: {
    x: 30 * unit, y: height - (bottom + captionHeight) * unit, width: width - (shortFeed ? 60 : 252) * unit,
    height: captionHeight * unit,
  } }, { name: 'Playback controls', kind: 'playback', bounds: { x: 0, y: height - 24 * unit, width, height: 24 * unit } });
  if (!shortFeed) regions.push({ name: 'Action buttons', kind: 'actions', bounds: {
    x: width - 174 * unit, y: Math.min(height * .44, 830 * unit), width: 140 * unit, height: Math.min(650 * unit, height * .38),
  } });
  return regions;
}

/** Native edge anchors from the reviewed layouts; guide margins remain advisory. */
export function socialLayoutInsets(target: PresentationTarget, width: number, height: number): Insets {
  if (target === 'none') {
    const unit = compositionOverlayUnit(width, height);
    return { left: 32 * unit, right: 32 * unit, top: 32 * unit, bottom: 24 * unit };
  }
  const unit = width / 1080;
  const regions = socialChromeRegions(target, width, height, 'normal');
  const caption = regions.find(region => region.kind === 'caption');
  // Reels shown in the feed crop the video vertically; retain avatar clearance there.
  const overlayOffset = target === 'instagram-reels' ? 64 * unit : 0;
  return { left: 64 * unit, right: (regions.some(region => region.kind === 'actions') ? 222 : 64) * unit,
    top: 210 * unit + overlayOffset,
    bottom: (caption ? height - caption.bounds.y + 36 * unit : 40 * unit) - overlayOffset };
}

/** Moves the edge groups proportionally while retaining their outer frame edges. */
export function socialPresentationInsets(presentation: SocialVideoPresentation, width: number, height: number): Insets {
  const insets = socialLayoutInsets(presentation.target, width, height);
  const unit = width / 1080;
  const upperOffset = presentation.layout === 'lower-title' ? 0 : presentation.upperVerticalOffset;
  return { ...insets, top: Math.max(0, insets.top + upperOffset * unit),
    bottom: Math.max(0, insets.bottom - presentation.lowerVerticalOffset * unit) };
}
